/**
 * The share link someone arrived on before they had an account, kept so the strategy is saved
 * once they are in — whatever sign-up took on the way (a popup, an email round trip, the guided
 * first-run). Written by the signed-out share page; read by onboarding (which builds the first
 * strategy from it when the plan covers it) and by the shared-strategy page (which saves it, and
 * clears this, the first time a signed-in visit finds it).
 *
 * This browser only. The other device in an email round trip is covered by the verification
 * link itself, which returns to `/s/<code>` (`auth/firebase.ts`), where the page writes it again.
 */
const KEY = 'sixone8.pendingShare';
// A link opened, abandoned and found again a month later should not save itself unasked.
const TTL_MS = 14 * 24 * 60 * 60 * 1000;

export function rememberShare(code: string) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ code: code.toUpperCase(), at: Date.now() }));
  } catch {
    // Storage can be blocked; the link itself still works, the save just needs a click.
  }
}

export function pendingShare(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { code, at } = JSON.parse(raw) as { code?: unknown; at?: unknown };
    if (typeof code !== 'string' || typeof at !== 'number' || Date.now() - at > TTL_MS) return null;
    return code;
  } catch {
    return null;
  }
}

export function forgetShare() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Non-fatal.
  }
}

/** The share code in a `/s/<code>` path, upper-cased, or null. */
export function shareCodeFromPath(path: string): string | null {
  const m = /^\/s\/([A-Za-z0-9]{4,32})\/?$/.exec(path);
  return m ? m[1]!.toUpperCase() : null;
}
